'use client'

import { tw } from 'utils/classnames'
import { CurrencyCode } from '@repo/graphql-types'
import { useCurrentCurrency } from '@repo/cf-user/hooks/use-current-currency'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { PAYMENT_ENTRY_POINTS } from 'payments/payments-dialog/analytics'
import { StartFreeTrialButton } from 'payments/start-free-trial-button'

interface TrialBarProps {
  title: string
  subtitle: string
  ctaLabel: string
  cancelLabel: string
}

export function TrialBar({ title, subtitle, ctaLabel, cancelLabel }: TrialBarProps) {
  const currency = useCurrentCurrency()
  const zeroPrice = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.code || CurrencyCode.Usd,
  }).format(0)

  return (
    <div className="bg-cf-white w-full overflow-hidden rounded-t-xl shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
      <div className="bg-violet-500 h-[3px] w-full" />
      <div className="mx-auto flex max-w-[1320px] flex-wrap items-center gap-3 px-5 py-3 md:gap-8 md:px-8 md:py-4">
        <div className="flex flex-1 flex-col gap-0.5 md:min-w-[280px]">
          <p className="text-18 text-cf-sf-gray-1050 md:text-24 font-bold leading-snug tracking-[-0.01em]">
            {title}
          </p>
          <p className="text-14 text-cf-grey-600">{subtitle}</p>
        </div>
        <div className="flex w-full shrink-0 flex-col items-center gap-2 md:w-auto">
          <StartFreeTrialButton
            className={tw(
              'bg-secondary-700 text-cf-white text-16 w-full rounded-lg px-8 py-3 font-semibold whitespace-nowrap md:w-auto md:py-[14px]',
              'transition-[filter] duration-[120ms] hover:brightness-90',
            )}
            label={`${ctaLabel} (${zeroPrice})`}
            subscriptionType="studio-ai"
            entryPoint={PAYMENT_ENTRY_POINTS.FLOATING_BANNER}
            onClick={() => {
              sendAmplitudeEvent(AmplitudeEvents.FREE_TRIAL_BUTTON_CLICKED)
            }}
          />
          <p className="text-12 text-cf-grey-600 font-medium whitespace-nowrap">{cancelLabel}</p>
        </div>
      </div>
    </div>
  )
}
